import { 
    WMS2010_R01ItemSubtotalData, 
    WMS2010_R01WarehouseSubtotalData, 
    WMS2010_R01LogicCenterSubtotalData, 
    WMS2010_R01ItemSubtotalRow, 
    WMS2010_R01WarehouseSubtotalRow, 
    WMS2010_R01LogicCenterSubtotalRow, 
    WMS2010_R01Dto,
    WMS2010_R01_SUBTOTAL_TYPES,
    WMS2010_R01_SUBTOTAL_LEVELS
} from '../Dto_WMS2010_R01';
import { defaultStockData } from 'src/dtos/stock/stockDto';

// 소계 계산 설정
interface SubtotalConfig {
    groupBy: string;
    displayFields: string[];
    aggregateFields: string[];
}

const WMS2010_R01_SUBTOTAL_CONFIGS: Record<string, SubtotalConfig> = {
    item: {
        groupBy: 'itemCd',
        displayFields: ['itemCd', 'itemNm', 'itemSpec', 'itemUnit', 'brandCd', 'brandNm', 'itemGroup', 'itemStatus'],
        aggregateFields: ['stockQty', 'reservQty', 'availableQty']
    },
    warehouse: {
        groupBy: 'itemCd',
        displayFields: ['itemCd', 'itemNm', 'itemSpec', 'itemUnit', 'itemGroup', 'itemStatus', 'logicCenter', 'logicCenterNm', 'warehouseCd', 'warehouseNm'],
        aggregateFields: ['stockQty', 'reservQty', 'availableQty']
    },
    logiccenter: {
        groupBy: 'itemCd',
        displayFields: ['itemCd', 'itemNm', 'itemSpec', 'itemUnit', 'itemGroup', 'itemStatus', 'logicCenter', 'logicCenterNm', 'warehouseCd', 'warehouseNm'],
        aggregateFields: ['stockQty', 'reservQty', 'availableQty']
    }
};

/**
 * 통합 소계 계산 함수
 */
const calculateSubtotals = <T extends any>(
    data: any[], 
    config: SubtotalConfig
): T[] => {
    if (!data || data.length === 0) return [];
    
    const grouped = data.reduce((acc, item) => {
        const key = item[config.groupBy];
        if (!acc[key]) {
            // 기본 필드들 초기화
            const baseData: any = {
                [config.groupBy]: item[config.groupBy],
                count: 0,
                groupKey: key
            };
            
            // 표시 필드들 초기화 (첫 번째 아이템의 값 사용)
            config.displayFields.forEach(field => {
                baseData[field] = item[field] || '';
            });
            
            // 집계 필드들 초기화
            config.aggregateFields.forEach(field => {
                baseData[field] = 0;
            });
            
            acc[key] = baseData;
        }
        
        // 카운트 증가
        acc[key].count += 1;
        
        // 집계 필드들 누적
        config.aggregateFields.forEach(field => {
            acc[key][field] += Number(item[field]) || 0;
        });
        
        return acc;
    }, {} as Record<string, T>);
    
    return (Object.values(grouped) as T[]).sort((a, b) => 
        (a as any)[config.groupBy].localeCompare((b as any)[config.groupBy])
    );
};

/**
 * 품목별 소계 데이터 계산 (품목별 탭용)
 */
export const calculateItemSubtotals = (data: any[]): WMS2010_R01ItemSubtotalData[] => {
    return calculateSubtotals<WMS2010_R01ItemSubtotalData>(data, WMS2010_R01_SUBTOTAL_CONFIGS.item);
};

/**
 * 창고별 소계 데이터 계산 (창고별 탭용)
 */
export const calculateWarehouseSubtotals = (data: any[]): WMS2010_R01WarehouseSubtotalData[] => {
    return calculateSubtotals<WMS2010_R01WarehouseSubtotalData>(data, WMS2010_R01_SUBTOTAL_CONFIGS.warehouse);
};

/**
 * 물류센터별 소계 데이터 계산 (물류센터별 탭용)
 */
export const calculateLogicCenterSubtotals = (data: any[]): WMS2010_R01LogicCenterSubtotalData[] => {
    return calculateSubtotals<WMS2010_R01LogicCenterSubtotalData>(data, WMS2010_R01_SUBTOTAL_CONFIGS.logiccenter);
};

/**
 * 품목별 소계 행 생성
 */
export const createItemSubtotalRow = (subtotal: WMS2010_R01ItemSubtotalData): WMS2010_R01ItemSubtotalRow => ({
    ...defaultStockData,
    isSubtotal: true,
    subtotalType: WMS2010_R01_SUBTOTAL_TYPES.ITEM,
    level: WMS2010_R01_SUBTOTAL_LEVELS.ITEM,
    groupKey: subtotal.itemCd,
    groupLabel: `${subtotal.itemCd} 소계 (${subtotal.count}건)`,
    count: subtotal.count,
    itemCd: subtotal.itemCd,
    itemNm: subtotal.itemNm,
    itemSpec: subtotal.itemSpec,
    itemUnit: subtotal.itemUnit,
    brandCd: subtotal.brandCd,
    brandNm: subtotal.brandNm,
    itemGroup: subtotal.itemGroup,
    itemStatus: subtotal.itemStatus,
    stockQty: subtotal.stockQty,
    reservQty: subtotal.reservQty,
    availableQty: subtotal.stockQty // 가용재고는 현재고를 사용
});

/**
 * 창고별 소계 행 생성
 */
export const createWarehouseSubtotalRow = (subtotal: WMS2010_R01WarehouseSubtotalData): WMS2010_R01WarehouseSubtotalRow => {
    const groupKey = subtotal.groupKey || subtotal.itemCd;
    return {
        ...defaultStockData,
        isSubtotal: true,
        subtotalType: WMS2010_R01_SUBTOTAL_TYPES.WAREHOUSE,
        level: WMS2010_R01_SUBTOTAL_LEVELS.WAREHOUSE,
        groupKey: groupKey,
        groupLabel: `${subtotal.itemCd} 소계 (${subtotal.count}건)`,
        count: subtotal.count,
        itemCd: subtotal.itemCd,
        itemNm: subtotal.itemNm,
        itemSpec: subtotal.itemSpec,
        itemUnit: subtotal.itemUnit,
        itemGroup: subtotal.itemGroup,
        itemStatus: subtotal.itemStatus,
        logicCenter: '', // 소계는 물류센터 구분 없음
        logicCenterNm: '', // 소계는 전체 물류센터
        warehouseCd: '', // 소계는 창고 구분 없음
        warehouseNm: '', // 소계는 전체 창고
        stockQty: subtotal.stockQty,
        reservQty: subtotal.reservQty,
        availableQty: subtotal.stockQty // 가용재고는 현재고를 사용
    };
};

/**
 * 물류센터별 소계 행 생성
 */
export const createLogicCenterSubtotalRow = (subtotal: WMS2010_R01LogicCenterSubtotalData): WMS2010_R01LogicCenterSubtotalRow => {
    const groupKey = subtotal.groupKey || subtotal.itemCd;
    return {
        ...defaultStockData,
        isSubtotal: true,
        subtotalType: WMS2010_R01_SUBTOTAL_TYPES.LOGICCENTER,
        level: WMS2010_R01_SUBTOTAL_LEVELS.LOGICCENTER,
        groupKey: groupKey,
        groupLabel: `${subtotal.itemCd} 소계 (${subtotal.count}건)`,
        count: subtotal.count,
        itemCd: subtotal.itemCd,
        itemNm: subtotal.itemNm,
        itemSpec: subtotal.itemSpec,
        itemUnit: subtotal.itemUnit,
        itemGroup: subtotal.itemGroup,
        itemStatus: subtotal.itemStatus,
        logicCenter: '', // 소계는 물류센터 구분 없음
        logicCenterNm: '', // 소계는 전체 물류센터
        warehouseCd: '', // 소계는 창고 구분 없음
        warehouseNm: '', // 소계는 전체 창고
        stockQty: subtotal.stockQty,
        reservQty: subtotal.reservQty,
        availableQty: subtotal.stockQty // 가용재고는 현재고를 사용
    };
};

/**
 * 상세 행 생성
 */
export const createDetailRow = (item: any): WMS2010_R01Dto => ({
    ...item,
    isSubtotal: false,
    level: WMS2010_R01_SUBTOTAL_LEVELS.DETAIL
});

/**
 * 소계 행 타입 가드 함수들
 */
export const isSubtotalRow = (row: WMS2010_R01Dto): row is WMS2010_R01ItemSubtotalRow | WMS2010_R01WarehouseSubtotalRow | WMS2010_R01LogicCenterSubtotalRow => {
    return row.isSubtotal === true;
};

export const isItemSubtotalRow = (row: WMS2010_R01Dto): row is WMS2010_R01ItemSubtotalRow => {
    return isSubtotalRow(row) && row.subtotalType === WMS2010_R01_SUBTOTAL_TYPES.ITEM;
};

export const isWarehouseSubtotalRow = (row: WMS2010_R01Dto): row is WMS2010_R01WarehouseSubtotalRow => {
    return isSubtotalRow(row) && row.subtotalType === WMS2010_R01_SUBTOTAL_TYPES.WAREHOUSE;
};

export const isLogicCenterSubtotalRow = (row: WMS2010_R01Dto): row is WMS2010_R01LogicCenterSubtotalRow => {
    return isSubtotalRow(row) && row.subtotalType === WMS2010_R01_SUBTOTAL_TYPES.LOGICCENTER;
};

/**
 * 데이터와 소계를 병합하는 함수
 */
export const mergeDataWithSubtotals = (
    originalData: any[],
    subtotals: any[],
    showSubtotals: boolean,
    tabType: 'item' | 'warehouse' | 'logiccenter' | 'total' | 'lot'
): WMS2010_R01Dto[] => {
    // 품목별, 전체, LOT 탭은 소계 표시하지 않음
    if (tabType === 'item' || tabType === 'total' || tabType === 'lot') {
        return originalData.map(createDetailRow);
    }
    
    if (!showSubtotals || !subtotals || subtotals.length === 0) {
        return originalData.map(createDetailRow);
    }

    const result: WMS2010_R01Dto[] = [];
    const subtotalMap = new Map(subtotals.map(s => [s.groupKey || s.itemCd, s]));

    // 원본 데이터를 그룹별로 정렬하고 소계 삽입
    const groupedData = originalData.reduce((acc, item) => {
        const groupKey = item.itemCd; // 품목코드만으로 그룹화

        if (!acc[groupKey]) {
            acc[groupKey] = [];
        }
        acc[groupKey].push(item);
        return acc;
    }, {} as Record<string, any[]>);

    // 각 그룹별로 상세 행 먼저 추가, 그 다음 소계 행 추가
    Object.keys(groupedData).sort().forEach(groupKey => {
        const subtotal = subtotalMap.get(groupKey);
        
        // 상세 행들 먼저 추가
        groupedData[groupKey].forEach((item: any) => {
            result.push(createDetailRow(item));
        });
        
        // 그 다음 소계 행 추가
        if (subtotal) {
            if (tabType === 'warehouse') {
                const subtotalRow = createWarehouseSubtotalRow(subtotal);
                result.push(subtotalRow);
            } else if (tabType === 'logiccenter') {
                const subtotalRow = createLogicCenterSubtotalRow(subtotal);
                result.push(subtotalRow);
            }
        }
    });

    return result;
};
